import { useLocation } from "@tanstack/react-router";

import type { BatchMethod } from "./batch-method";
import { tryParseBatchMethod } from "./batch-method";

export function isEmbedMode(): boolean {
  if (typeof window === "undefined") return false;

  const urlParams = new URLSearchParams(window.location.search);
  const embedParam = urlParams.get("embed");
  return embedParam === "true";
}

export function useEmbedMode(): boolean {
  const location = useLocation();
  const search = location.search as { embed?: boolean };
  if (search.embed === true) {
    return true;
  }

  // Child routes validate their own search slices; retain `embed` on the URL even when
  // it is absent from the active route's parsed search.
  return isEmbedMode();
}

export function getColorSchemeOverride(): "light" | "dark" | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const colorSchemeParam = urlParams.get("colorScheme");
  return colorSchemeParam === "light" || colorSchemeParam === "dark" ? colorSchemeParam : null;
}

export function getWidthOverride(): "mobile" | "tablet" | "desktop" | number | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const widthParam = urlParams.get("width");

  if (!widthParam) return null;

  if (widthParam === "mobile" || widthParam === "tablet" || widthParam === "desktop") {
    return widthParam;
  }

  const numericWidth = Number.parseInt(widthParam, 10);
  if (!Number.isNaN(numericWidth) && numericWidth >= 375 && numericWidth <= 1366) {
    return numericWidth;
  }

  return null;
}

export function getHeightOverride(): "mobile" | "tablet" | "desktop" | number | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const heightParam = urlParams.get("height");

  if (!heightParam) return null;

  if (heightParam === "mobile" || heightParam === "tablet" || heightParam === "desktop") {
    return heightParam;
  }

  const numericHeight = Number.parseInt(heightParam, 10);
  if (!Number.isNaN(numericHeight) && numericHeight >= 480 && numericHeight <= 1200) {
    return numericHeight;
  }

  return null;
}

export function getCurrencyOverride(): "USD" | "KRW" | "JPY" | null {
  if (typeof window === "undefined") return null;

  const supportedCurrencies: ("USD" | "KRW" | "JPY")[] = ["USD", "KRW", "JPY"];

  const urlParams = new URLSearchParams(window.location.search);
  const urlCurrency = urlParams.get("currency");

  if (urlCurrency && supportedCurrencies.includes(urlCurrency as "USD" | "KRW" | "JPY")) {
    return urlCurrency as "USD" | "KRW" | "JPY";
  }

  // localStorage throws SecurityError in Safari private browsing and cross-origin iframes.
  let savedCurrency: string | null = null;
  try {
    savedCurrency = localStorage.getItem("widget-currency");
  } catch {
    // Storage access denied — treat as no saved preference.
  }
  if (savedCurrency && supportedCurrencies.includes(savedCurrency as "USD" | "KRW" | "JPY")) {
    return savedCurrency as "USD" | "KRW" | "JPY";
  }

  return null;
}

export function getLocaleOverride(): "en-US" | "ko-KR" | "ja-JP" | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const localeParam = urlParams.get("locale");

  if (!localeParam) return null;

  const supportedLocales: ("en-US" | "ko-KR" | "ja-JP")[] = ["en-US", "ko-KR", "ja-JP"];

  if (supportedLocales.includes(localeParam as "en-US" | "ko-KR" | "ja-JP")) {
    return localeParam as "en-US" | "ko-KR" | "ja-JP";
  }

  return null;
}

export function getWalletModeOverride(): "internal" | "external" | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const walletModeParam = urlParams.get("walletMode");
  return walletModeParam === "internal" || walletModeParam === "external" ? walletModeParam : null;
}

export function getBatchModeOverride(): BatchMethod | "off" | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const batchModeParam = urlParams.get("batchMode");
  if (!batchModeParam) return null;
  if (batchModeParam.toLowerCase() === "off") return "off";
  return tryParseBatchMethod(batchModeParam);
}

export function getBatcherContractOverride(): string | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const param = urlParams.get("batcherContract");
  if (!param) return null;

  return /^0x[a-fA-F0-9]{40}$/.test(param) ? param : null;
}

export function getAtomicRequiredOverride(): boolean | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const param = urlParams.get("atomicRequired");

  if (param === "true") return true;
  if (param === "false") return false;

  return null;
}

export function getEnableFeeOverride(): boolean | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const param = urlParams.get("enableFee");

  if (param === "true") return true;
  if (param === "false") return false;

  return null;
}

export function isInIframe(): boolean {
  if (typeof window === "undefined") return false;

  return window !== window.top;
}
